/**
 * Wind Barb Legend Content
 * Static legend explaining meteorological wind barb symbols.
 * Shown for ABB weather layers that use barb/arrow symbology
 * (these layers have no color-table legend from the API).
 */

import React, { useMemo } from 'react';
import { convertSpeed } from '@ais-ui/utils';
import { useUnitSystem } from '../../../Units/useUnitSystem';

// Wind barbs are defined in knots per WMO standard.
// Each entry describes a barb example: its speed in knots, a label, and SVG elements.
const BARB_EXAMPLES = [
  {
    knots: 0,
    svg: (
      <circle
        cx="20"
        cy="12"
        r="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    knots: 5,
    description: 'one short tick',
    svg: (
      <>
        <line
          x1="4"
          y1="12"
          x2="32"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="32"
          y1="12"
          x2="28"
          y2="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    knots: 10,
    description: 'one long tick',
    svg: (
      <>
        <line
          x1="4"
          y1="12"
          x2="32"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="32"
          y1="12"
          x2="26"
          y2="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    knots: 25,
    description: 'two long + one short',
    svg: (
      <>
        <line
          x1="4"
          y1="12"
          x2="32"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="32"
          y1="12"
          x2="26"
          y2="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="28"
          y1="12"
          x2="22"
          y2="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="24"
          y1="12"
          x2="20"
          y2="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    knots: 50,
    description: 'one flag (filled triangle)',
    svg: (
      <>
        <line
          x1="4"
          y1="12"
          x2="32"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polygon
          points="32,12 32,3 26,12"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.5"
        />
      </>
    ),
  },
  {
    knots: 65,
    description: 'one flag + one long + one short',
    svg: (
      <>
        <line
          x1="4"
          y1="12"
          x2="32"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polygon
          points="32,12 32,3 26,12"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.5"
        />
        <line
          x1="23"
          y1="12"
          x2="17"
          y2="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="19"
          y1="12"
          x2="15"
          y2="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
] as const;

// Knot values for the "how to read" key at the bottom
const SHORT_TICK_KN = 5;
const LONG_TICK_KN = 10;
const FLAG_KN = 50;

/**
 * Wind barb SVG illustrations with speed labels adapted to
 * the user's chosen unit system (kn / mph / m/s).
 */
export const WindBarbLegendContent: React.FC = () => {
  const unitSystem = useUnitSystem();

  const { unit, shortTick, longTick, flag, examples } = useMemo(() => {
    const s = convertSpeed(SHORT_TICK_KN, 'kn', unitSystem);
    return {
      unit: s.unit,
      shortTick: Math.round(s.value),
      longTick: Math.round(convertSpeed(LONG_TICK_KN, 'kn', unitSystem).value),
      flag: Math.round(convertSpeed(FLAG_KN, 'kn', unitSystem).value),
      examples: BARB_EXAMPLES.map((b) => ({
        ...b,
        displayValue:
          b.knots === 0
            ? null
            : Math.round(convertSpeed(b.knots, 'kn', unitSystem).value),
      })),
    };
  }, [unitSystem]);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Line points in the direction wind is coming from.
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {examples.map((barb) => (
          <div key={barb.knots} className="flex items-center gap-2">
            <svg
              width="40"
              height="24"
              viewBox="0 0 40 24"
              className="shrink-0 text-foreground"
            >
              {barb.svg}
            </svg>
            <span className="text-xs text-muted-foreground">
              {barb.displayValue != null
                ? `${barb.displayValue} ${unit}`
                : 'Calm'}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 pt-1 border-t border-border">
        <svg width="16" height="16" viewBox="0 0 16 16" className="shrink-0">
          <line
            x1="4"
            y1="12"
            x2="4"
            y2="4"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <line
            x1="4"
            y1="4"
            x2="9"
            y2="7"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
        <span className="text-xs text-muted-foreground">
          Short tick = {shortTick} {unit}, long tick = {longTick} {unit}, flag ={' '}
          {flag} {unit}
        </span>
      </div>
    </div>
  );
};
